/**
 * Homepage guest dock (tiers T7, Beta-gated) — the approved front-page
 * design's "try it now" entry: type a prompt, pass the invisible reCAPTCHA
 * screen, land in the guest console with the prompt as turn 1. Engaging the
 * dock selects Uno in the hero (guests chat with Uno only).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  GuestStartError,
  loadGuestSession,
  startGuestSession,
  stashPendingPrompt,
} from '../agents/guestApi.ts';
import { preloadRecaptcha } from '../auth/recaptcha.ts';
import './GuestDock.css';

interface Props {
  /** Select Uno in the hero when the visitor engages the dock. */
  readonly onEngage: () => void;
  /** Desktop autofocus (approved UX: focus the field on load). */
  readonly autoFocus: boolean;
  /** Mobile swaps to the shorter placeholder (approved mock). */
  readonly isMobile: boolean;
}

export default function GuestDock({ onEngage, autoFocus, isMobile }: Props) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    preloadRecaptcha();
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = prompt.trim();
    if (trimmed.length === 0 || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Reuse a still-valid session from this tab; otherwise mint one
      // (one reCAPTCHA assessment per mint).
      if (loadGuestSession() === null) {
        await startGuestSession();
      }
      stashPendingPrompt(trimmed);
      navigate('/guest');
    } catch (cause: unknown) {
      setError(
        cause instanceof GuestStartError
          ? cause.message
          : 'Guest chat is temporarily unavailable. Please try again shortly.',
      );
      setBusy(false);
    }
  };

  return (
    <div className={busy ? 'om-guest-dock om-guest-dock--connecting' : 'om-guest-dock'}>
      {/* Pinned above the field's trailing edge. Absolutely positioned, so it
          adds no height and the input keeps its exact layout box — see
          mockups/homepage-briefs-nav-pill, which measures the field before and
          after and fails on any delta. Plain <a> for the same reason as the
          header link.

          Label and arrow only. It previously carried a ◉ and the catalog count
          as an accent badge, and both were removed on report: the count was an
          unlabelled number next to a link, so it read as a notification badge
          — something new to look at — rather than "seven briefs exist", which
          is not a fact a first-time visitor needs before clicking. The dot
          decorated a control that is already visibly a control. */}
      <a className="om-guest-dock__briefs" href="/widgets">
        Research briefs
        <span aria-hidden="true">→</span>
      </a>
      <form
        className="om-guest-input"
        onSubmit={(e) => {
          void submit(e);
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={prompt}
          maxLength={4000}
          placeholder={
            isMobile
              ? 'Ask Uno anything — free, no account'
              : 'Ask Uno anything — weather, markets, the web…'
          }
          aria-label="Ask Uno anything"
          disabled={busy}
          onChange={(e) => setPrompt(e.target.value)}
          onFocus={onEngage}
          onClick={onEngage}
        />
        <button
          className="om-guest-send"
          type="submit"
          aria-label="Send"
          disabled={busy || prompt.trim().length === 0}
        >
          {busy ? <span className="om-guest-send__spin" aria-hidden="true" /> : '➤'}
        </button>
      </form>
      {/* Agreement consent by action (no checkbox): sending a message IS the
          acceptance, so the notice must sit adjacent to the send control and
          name the documents it binds. Mirrors the sign-up consent set. */}
      <p className="om-guest-dock__consent">
        By sending a message, you agree to our <Link to="/legal/terms">Terms of Service</Link>,{' '}
        <Link to="/legal/privacy">Privacy Policy</Link>, and{' '}
        <Link to="/legal/ai-consent">AI Notice</Link>. Ads keep guest access free.
      </p>
      {busy ? (
        <p className="om-guest-dock__status" role="status">
          <span className="om-guest-dock__status-dot" aria-hidden="true">
            ◉
          </span>{' '}
          ENGAGING UNO<span className="om-guest-dock__status-dots" aria-hidden="true" />
        </p>
      ) : null}
      {error ? (
        <p className="om-guest-dock__error" role="alert">
          {error} <Link to="/auth/signup">Create a free account</Link>
        </p>
      ) : null}
      <p className="om-guest-dock__recaptcha">
        This site is protected by reCAPTCHA and the Google{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{' '}
        apply.
      </p>
    </div>
  );
}
